/**
 * Admin collaborator detail page.
 * Shows detailed information about a workspace collaborator with ability to edit role,
 * view contact info, and see project associations.
 */

import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useAdminCollaboratorDetail,
  useAdminUpdateCollaboratorRole,
} from "../../hooks/useAdminWorkspaces";
import { PageHeader } from "../../components/layout/PageHeader";
import { WorkspaceRoleBadge, getRoleDescription } from "../../components/WorkspaceRoleBadge";
import type { WorkspaceCollaboratorRole, CollaboratorProjectAssociation } from "../../services/naraDoor";
import { callableErrorCode } from "../../lib/invite";
import { adminRoutes } from "../../lib/routes";
import {
  Users,
  Mail,
  Phone,
  FolderKanban,
  Check,
  ArrowLeft,
  AlertCircle,
} from "lucide-react";

// No "owner": ownership comes only from self-signup, the server refuses it (T-200).
const CHANGEABLE_ROLES: WorkspaceCollaboratorRole[] = ["admin", "editor", "viewer", "uploader"];

export function AdminCollaboratorDetail() {
  const { t } = useTranslation();
  const { workspaceId, userId } = useParams<{ workspaceId: string; userId: string }>();
  const navigate = useNavigate();
  const { data: collaboratorData, isLoading } = useAdminCollaboratorDetail(
    workspaceId,
    userId
  );
  const updateCollaboratorMutation = useAdminUpdateCollaboratorRole();
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);

  const getDisplayName = () => {
    if (collaboratorData?.first_name && collaboratorData?.last_name) {
      return `${collaboratorData.first_name} ${collaboratorData.last_name}`;
    }
    if (collaboratorData?.first_name) {
      return collaboratorData.first_name;
    }
    return collaboratorData?.email || t("adminCollaborator.unknown");
  };

  const getInitials = () => {
    if (collaboratorData?.first_name && collaboratorData?.last_name) {
      return `${collaboratorData.first_name[0]}${collaboratorData.last_name[0]}`.toUpperCase();
    }
    if (collaboratorData?.first_name) {
      return collaboratorData.first_name.substring(0, 2).toUpperCase();
    }
    if (collaboratorData?.email) {
      return collaboratorData.email.substring(0, 2).toUpperCase();
    }
    return "??";
  };

  const handleRoleChange = (role: WorkspaceCollaboratorRole) => {
    if (!userId || !workspaceId) return;
    updateCollaboratorMutation.mutate(
      { workspaceId, userId, role },
      { onError: (err) => console.error("adminUpdateWorkspaceCollaboratorRole failed", { workspaceId, userId, code: callableErrorCode(err) }) }
    );
    setIsRoleDialogOpen(false);
  };

  const backUrl = `/admin/workspaces/${workspaceId}`;

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title={t("common.loading")}
          isAdmin
          breadcrumbs={[
            { label: t("nav.adminSection") },
            { label: t("admin.workspaces"), href: "/admin/workspaces" },
            { label: "...", href: backUrl },
            { label: t("nav.collaborators") },
          ]}
        />
        <div className="space-y-6">
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="animate-pulse space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-muted" />
                <div className="space-y-2">
                  <div className="h-6 w-48 bg-muted rounded" />
                  <div className="h-4 w-24 bg-muted rounded" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="h-16 bg-muted rounded" />
                <div className="h-16 bg-muted rounded" />
                <div className="h-16 bg-muted rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!collaboratorData) {
    return (
      <div>
        <PageHeader
          title={t("adminCollaborator.notFound")}
          isAdmin
          breadcrumbs={[
            { label: t("nav.adminSection") },
            { label: t("admin.workspaces"), href: "/admin/workspaces" },
            { label: t("adminCollaborator.workspace"), href: backUrl },
            { label: t("adminCollaborator.notFoundBreadcrumb") },
          ]}
        />
        <div className="bg-card rounded-lg border border-border p-12 text-center">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t("adminCollaborator.notFoundDescription")}</p>
          <button
            onClick={() => navigate(backUrl)}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-700 dark:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("adminCollaborator.backToWorkspace")}
          </button>
        </div>
      </div>
    );
  }

  const currentRole = collaboratorData.workspace_role || null;
  const displayName = getDisplayName();

  return (
    <div>
      <PageHeader
        title={displayName}
        isAdmin
        breadcrumbs={[
          { label: t("nav.adminSection") },
          { label: t("admin.workspaces"), href: "/admin/workspaces" },
          { label: t("adminCollaborator.workspace"), href: backUrl },
          { label: displayName },
        ]}
      />

      <div className="space-y-6">
        {/* Header Card */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <span className="text-2xl font-medium text-blue-700 dark:text-blue-300">
                {getInitials()}
              </span>
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-foreground">
                {displayName}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <WorkspaceRoleBadge role={currentRole} />
                {collaboratorData.is_on_any_project && (
                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300">
                    {t("adminCollaborator.onProjects")}
                  </span>
                )}
                {currentRole !== "inactive" && (
                  <button
                    type="button"
                    onClick={() => setIsRoleDialogOpen(true)}
                    disabled={updateCollaboratorMutation.isPending}
                    className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300 hover:underline disabled:opacity-50"
                  >
                    {t("adminCollaborator.changeRole")}
                  </button>
                )}
              </div>
            </div>
          </div>
          {updateCollaboratorMutation.error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg mt-4">
              <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
              <p className="text-sm text-red-800 dark:text-red-200">{updateCollaboratorMutation.error.message}</p>
            </div>
          )}

          {/* Contact Info Grid */}
          <div className="grid gap-4 sm:grid-cols-2 mt-6">
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">{t("adminCollaborator.email")}</p>
              <p className="font-medium text-foreground flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                {collaboratorData.email || "—"}
              </p>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">{t("adminCollaborator.phone")}</p>
              <p className="font-medium text-foreground flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                {collaboratorData.phone || "—"}
              </p>
            </div>
          </div>
        </div>

        {/* Projects Section */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">
            {t("adminCollaborator.projects")} ({collaboratorData.projects?.length || 0})
          </h3>
          {collaboratorData.projects && collaboratorData.projects.length > 0 ? (
            <div className="space-y-3">
              {collaboratorData.projects.map((project: CollaboratorProjectAssociation) => (
                <Link
                  key={project.project_id}
                  to={adminRoutes.workspaceProject(workspaceId!, project.project_id)}
                  className="block"
                >
                  <div className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <FolderKanban className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium text-foreground">
                        {project.project_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {project.email_transcription && (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                          {t("adminCollaborator.emailReport")}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">
              {t("adminCollaborator.noProjects")}
            </p>
          )}
        </div>
      </div>

      {/* Role Change Dialog */}
      {isRoleDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-foreground mb-2">
                {t("adminCollaborator.changeRoleTitle")}
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                {t("adminCollaborator.changeRoleDescription")}
              </p>
              <div className="space-y-2">
                {CHANGEABLE_ROLES.map((role) => (
                  <button
                    key={role}
                    onClick={() => handleRoleChange(role)}
                    disabled={updateCollaboratorMutation.isPending || currentRole === role}
                    className={`w-full text-left p-4 rounded-lg border transition-colors ${
                      currentRole === role
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-950"
                        : "border-border hover:bg-accent"
                    } disabled:opacity-50`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <WorkspaceRoleBadge role={role} />
                        {currentRole === role && (
                          <span className="text-xs text-muted-foreground">({t("adminCollaborator.current")})</span>
                        )}
                      </div>
                      {currentRole === role && (
                        <Check className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      {getRoleDescription(role)}
                    </p>
                  </button>
                ))}
              </div>
              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setIsRoleDialogOpen(false)}
                  className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
